import React, { useContext, useEffect, useMemo, useState } from "react";
import Topic from "./TopicItem";
import TabBars from "./TabBars"
import { filterTabs } from "./tabs";
import { CircularProgress, Dialog, ListItemIcon, ListItemText, Menu, MenuItem, Slide, Snackbar, Typography } from "@mui/material";
import { IGroupUsers, ITopic, ITab, ITabKey, UserRoles } from "../../types";
import GIcon from "../GIcon";
import { Context } from "../ChatPanel/ChatContext";
import { useAtomInstance, useAtomSelector, useAtomState, useAtomValue, useEcosystem } from "@zedux/react";
import { getVisibleUsers } from "../../selector";
import { activeTopicAtom, topicIsLoadingAtom } from "../../atoms/atoms";
import { getSortedTopicsByTab } from '../../getSortedTopicsByTab';
import { topicsAtom } from '../../atoms/topicsAtom';
import { NewTopicDialog } from "./NewTopicDialog";
import { NewGroupDialog } from "./NewGroupDialog";
import { userByIdSelector, usersByCategorySelector, usersMapAtom } from "../../atoms/userListAtom";
import cx from "classix";
import { contactsListAtom } from "@src/atoms/socket";
import useModalState from "@src/library/useModalState";
import { EmptyShareContent, IShareContentState, setShareContent, shareContentAtom } from "@src/atoms/shareContentAtom";
import { typeAreaAtom } from "@src/atoms/typeAreaAtoms";
import base64ToFile from "@src/functions/base64ToFile";

const rolesThatCanCreateGroup = [UserRoles.ADMIN, UserRoles.SUPER_ADMIN, UserRoles.VENDOR, UserRoles.STAFF]

export default function Topics({ }: {}) {
    const { context, userInfo } = useContext(Context);

    const canCreateGroup = rolesThatCanCreateGroup.includes(userInfo.role as any) && context === "chat"
    const usersInGroups = useAtomValue(contactsListAtom);

    const filteredTabs = useMemo<ITab[]>(() => filterTabs(context, userInfo), [context, userInfo]);
    const [tab, setTab] = useState<ITabKey | null>(null);

    const ecosystem = useEcosystem()


    useEffect(() => {
        if (tab === null && filteredTabs.length > 0) {
            setTab(filteredTabs[0].key as ITabKey);
        } else if (tab !== null && !filteredTabs.some(t => t.key === tab)) {
            setTab(filteredTabs.length > 0 ? filteredTabs[0].key as ITabKey : null);
        }
    }, [context, userInfo]);

    const isLoading = useAtomValue(topicIsLoadingAtom);
    const topics = useAtomSelector(getSortedTopicsByTab, context, tab);

    const [searchTerm, setSearchTerm] = useState('');
    const filteredTopics = filterOutTopics(topics, searchTerm);
    const notEmpty = Array.isArray(topics) && topics.filter(Boolean).length > 0;

    // --- menu ---
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);

    // --- dialog visibility ---
    const [pickTopic, openPickTopic, closePickTopic] = useModalState(false);
    const [pickGroup, openPickGroup, closePickGroup] = useModalState(false);
    const [isChannel, setIsChannel] = useState(false);

    // Sure to share?
    const [sureToShare, openSureToShare, closeSureToShare] = useModalState<ITopic | false>(false);

    // --- snackbar ---
    const [snackError, setSnackError] = useState("");

    // --- atoms ---
    const { addOrUpdateTopic } = useAtomInstance(topicsAtom, [context]).exports;
    const [, setActiveTopicID] = useAtomState(activeTopicAtom, [context]);

    // const handleMenuOpenTopic = () => {
    //     openPickTopic()
    //     setAnchorEl(null)
    // }

    const handleMenuOpenGroup = () => {
        setIsChannel(false)
        openPickGroup()
        setAnchorEl(null)
    }

    const handleMenuOpenChannel = () => {
        setIsChannel(true)
        openPickGroup()
        setAnchorEl(null)
    }

    const handleTopicCreated = (topic: ITopic) => {
        addOrUpdateTopic(topic)
        setActiveTopicID(topic.topic_id)
    }

    const handleGroupCreated = (topic: ITopic) => {
        addOrUpdateTopic(topic)
        setActiveTopicID(topic.topic_id)
    }

    const [sharedContent, setShareContentState] = useAtomState(shareContentAtom)

    const handleTopicClick = (topic: ITopic) => {
        if (sharedContent !== EmptyShareContent) {
            openSureToShare(topic)
        } else {
            if (topic.restricted) {
                const showUpgradeSubscriptionPopup = (globalThis as typeof globalThis & {
                    showUpgradeSubscriptionPopup?: () => void
                }).showUpgradeSubscriptionPopup;

                if (showUpgradeSubscriptionPopup != null) {
                    showUpgradeSubscriptionPopup()
                }
                return;
            }
            setActiveTopicID(topic.topic_id)
        }
    }

    const handleSureToShare = () => {
        if (sureToShare && typeof sureToShare === "object" && sureToShare.topic_id && sharedContent !== EmptyShareContent) {
            setActiveTopicID(sureToShare.topic_id)
            const typeAreaAtomInstance = ecosystem.getInstance(typeAreaAtom, [context, sureToShare.topic_id])
            if (typeAreaAtomInstance) {
                typeAreaAtomInstance.exports.setFileData(base64ToFile(sharedContent as IShareContentState))
                setShareContentState(EmptyShareContent)
            }
        }
        closeSureToShare()
    }

    return (
        <div className={cx(
            "bg-white  border-neutral-40 border-solid",
            'flex flex-col max-lg:in-[.topic-selected]:hidden',
            'w-full lg:in-[.topic-empty]:w-88 lg:min-w-60',
            'lg:in-[.topic-selected]:w-88 lg:border-l lg:pl-3',
            'relative',
        )}>
            <Slide direction="up">
                <Snackbar
                    open={!!snackError}
                    autoHideDuration={3000}
                    onClose={() => setSnackError("")}
                    message={snackError}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                />
            </Slide>

            {filteredTabs.length > 0 && (
                <TabBars tabs={filteredTabs} tab={tab} setTab={setTab} context={context} />
            )}

            {context === "chat" && (
                <div className="flex flex-row justify-between items-center gap-4 my-2 px-2">
                    <div className="relative grow">
                        <div className="top-0 right-0 bottom-0 absolute flex items-center text-stone-500 select-none">
                            <GIcon name="search" />
                        </div>
                        <input
                            name="topicFilter"
                            className="bg-input/5 focus-within:bg-input/15 disabled:bg-input/25 p-2 pr-7 border border-input/20 focus-within:border-input/30 border-solid rounded-lg outline-0 focus:outline-none w-full disabled:text-secondary-02 read-only:text-secondary-02 text-base"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="جستجو"
                        />
                    </div>
                </div>
            )}

            <NewTopicDialog
                open={pickTopic}
                onClose={closePickTopic}
                context={context}
                onTopicCreated={handleTopicCreated}
            />

            <Dialog
                sx={{
                    "& .MuiDialog-container": {
                        "& .MuiPaper-root": {
                            width: "100%",
                            maxWidth: "36rem",
                            borderRadius: "1rem",
                        },
                    },
                }}
                disableScrollLock
                open={pickGroup}
                onClose={closePickGroup}
            >
                <NewGroupDialog
                    open={pickGroup}
                    onClose={closePickGroup}
                    context={context}
                    isChannel={isChannel}
                    onGroupCreated={handleGroupCreated}
                />
            </Dialog>

            <Dialog
                sx={{
                    "& .MuiDialog-container": {
                        "& .MuiPaper-root": {
                            outline: "none",
                        },
                    },
                }}
                disableScrollLock
                open={sureToShare !== false}
                onClose={closeSureToShare}
            >
                <div className="flex flex-col gap-4 p-6">
                    آیا موافق اشتراک گذاری این محتوا در این گفت‌وگو هستید؟
                    <div className="flex flex-row justify-center gap-4 mt-4">
                        <button className="bg-primary-00 text-white px-4 py-2 rounded-lg" onClick={handleSureToShare}>بله</button>
                        <button className="bg-neutral-20 text-neutral-900 px-4 py-2 rounded-lg" onClick={closeSureToShare}>خیر</button>
                    </div>
                </div>
            </Dialog>



            {isLoading ? (
                <CircularProgress className="m-auto" />
            ) : notEmpty ? (
                <div className="h-full overflow-x-hidden overflow-y-auto">
                    {filteredTopics.map((topic, i) =>
                        <Topic key={i}
                            topic={topic}
                            guests={usersInGroups?.guests}
                            onClick={() => handleTopicClick(topic)}
                        />)}
                </div>
            ) : (
                <div className="m-auto text-stone-500/60 text-xl">هیچ گفت‌وگویی پیدا نشد!</div>
            )}

            {canCreateGroup &&

                <div className="bottom-1 left-1 absolute flex flex-row-reverse bg-white rounded-full p-0">
                    <GIcon
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
                        className="text-5xl! text-primary-00 cursor-pointer"
                        name="add_circle"
                    />
                    <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={() => setAnchorEl(null)}
                        disableScrollLock
                        slotProps={{ list: { 'aria-labelledby': 'basic-button' } }}
                    >
                        {/* <MenuItem onClick={handleMenuOpenTopic}>
                        <ListItemIcon><GIcon className="ml-2" name="person" /></ListItemIcon>
                        <ListItemText><span>ارسال پیام شخصی</span></ListItemText>
                    </MenuItem> */}
                        <MenuItem onClick={handleMenuOpenGroup}>
                            <ListItemIcon><GIcon className="ml-2" name="person_add" /></ListItemIcon>
                            <ListItemText><span>ایجاد گروه گفت‌وگو</span></ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleMenuOpenChannel}>
                            <ListItemIcon><GIcon className="ml-2" name="campaign" /></ListItemIcon>
                            <ListItemText><span>ایجاد کانال گفت‌وگو</span></ListItemText>
                        </MenuItem>
                    </Menu>
                </div>
            }
        </div>
    );
}


function filterOutTopics(topics: ITopic[], searchTerm: string): ITopic[] {
    const users = useAtomInstance(usersMapAtom)
    if (!searchTerm) return topics

    const userMap = users.getState() || {}
    const term = searchTerm.toLowerCase()

    return topics.filter(({ topic_name, other_display = '', other_user_id }) => topic_name.toLowerCase().includes(term) || other_display.toLowerCase().includes(term) ||
        (other_user_id && userMap[other_user_id] && userMap[other_user_id]['fullname'] && userMap[other_user_id]['fullname'].includes(term)) ||
        (other_user_id && userMap[other_user_id] && userMap[other_user_id]['username'] && userMap[other_user_id]['username'].includes(term))
    );
}